import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { ConfigProvider, message } from 'antd';
import designServices from '../../../../services/oneButtonServices/designServices';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import { COMMON_CONSTANTS, useOneButtonStore, type ChangeOrderLoanStatusRow } from '../../../../store/oneButtonStore';
import { useViewerStore } from '../../../../store/store';
import { loanRowHasChangeOrderFlag } from './commerceGuideLogic';
import { UPDATE_LOAN_STATUS_SYSTEM_DESIGN_FIELD } from './BatteryDetailsModal';
import SystemDesignForceSyncConfirmModal from './SystemDesignForceSyncConfirmModal';
import { buildAntdThemeConfig, resolveAppPrimaryColor } from '../../../../helpers/antdThemeConfig';

/** Trinity / Angular `CheckNewDesignExist` localStorage gate (system-design refresh + force sync). */
export const CHECK_NEW_DESIGN_EXIST_STORAGE_KEY = 'CheckNewDesignExist';

export const SIGNIFICANT_CHANGE_ORDER_STATUSES = new Set(['Signed', 'Sent', 'Generated']);

/** Above viewer finalize modals (`z-index: 999999`). */
const VIEWER_FORCE_SYNC_CONFIRM_Z_INDEX = 1_000_001;

function normPartnerName(s: string | undefined | null): string {
    return String(s ?? '')
        .replace(/\s+/g, '')
        .toLowerCase();
}

export function normalizeLoanStatusRows(raw: unknown): ChangeOrderLoanStatusRow[] {
    if (Array.isArray(raw)) return raw as ChangeOrderLoanStatusRow[];
    if (raw != null && typeof raw === 'object') return [raw as ChangeOrderLoanStatusRow];
    return [];
}

export function clearCheckNewDesignExistFromStorage(numericOpportunityId: string | null | undefined): void {
    if (typeof window === 'undefined') return;
    try {
        localStorage.removeItem(CHECK_NEW_DESIGN_EXIST_STORAGE_KEY);
        if (numericOpportunityId) {
            localStorage.removeItem(`${CHECK_NEW_DESIGN_EXIST_STORAGE_KEY}_${numericOpportunityId}`);
        }
    } catch {
        /* ignore */
    }
}

export function opportunityHasSolarFinancePartner(
    opportunityDetailsArg: any,
    partnerDisplayName: string,
): boolean {
    const op = opportunityDetailsArg?.OpportunityProduct ?? opportunityDetailsArg?.opportunityProduct;
    if (!Array.isArray(op)) return false;
    const target = normPartnerName(partnerDisplayName);
    return op.some(
        (product: any) =>
            product.Product_Type_Name === COMMON_CONSTANTS.SolarProductName &&
            normPartnerName(product.Finance_Partner) === target,
    );
}

function findLoanRowForPartner(rows: ChangeOrderLoanStatusRow[], partnerDisplayName: string): ChangeOrderLoanStatusRow | undefined {
    const target = normPartnerName(partnerDisplayName);
    return rows.find((x) => normPartnerName(x.Finance_Partner_Name ?? x.finance_Partner_Name) === target);
}

export function shouldPromptForceSyncBeforeSystemDesignRefresh(params: {
    opportunityDetailsArg: any;
    loanRows: ChangeOrderLoanStatusRow[];
}): boolean {
    const { opportunityDetailsArg, loanRows } = params;

    const partnersToCheck: string[] = [];
    if (opportunityHasSolarFinancePartner(opportunityDetailsArg, COMMON_CONSTANTS.LightreachPartnerName)) {
        partnersToCheck.push(COMMON_CONSTANTS.LightreachPartnerName);
    }
    if (opportunityHasSolarFinancePartner(opportunityDetailsArg, COMMON_CONSTANTS.GoodLeapPartnerName)) {
        partnersToCheck.push(COMMON_CONSTANTS.GoodLeapPartnerName);
    }

    for (const partnerName of partnersToCheck) {
        const row = findLoanRowForPartner(loanRows, partnerName);
        if (!row || !loanRowHasChangeOrderFlag(row)) continue;
        const status = String(row.application_Status ?? '').trim();
        if (SIGNIFICANT_CHANGE_ORDER_STATUSES.has(status)) return true;
    }
    return false;
}

export function resolveIsEquipmentModified(
    systemDesign: any,
    isBatteryInfoUpdated = false,
): boolean {
    return !!(
        systemDesign?.IsEquipmentModified &&
        !systemDesign?.IsWithinTolerance &&
        !systemDesign?.IsOutOfTolerance &&
        !isBatteryInfoUpdated
    );
}

export type RunRetrieveSaveAfterForceSync = (
    consumeNewDesignFlag: boolean,
    disabledCreateLead?: boolean,
) => Promise<void>;

type FinishForceSyncParams = {
    confirmed: boolean;
    opportunityId: string;
    opportunityDetails: any;
    isEquipmentModified: boolean;
    runRetrieveSave: RunRetrieveSaveAfterForceSync;
};

/**
 * Trinity `ProductionsCard.finishForceSyncConfirm` — void LR quote, loan-status override, page changes, optional save-on-cancel.
 * Returns whether the caller should continue with the main retrieve/save path (`true` on OK sync path).
 */
export async function finishSystemDesignForceSyncConfirm(params: FinishForceSyncParams): Promise<boolean> {
    const { confirmed, opportunityId, opportunityDetails, isEquipmentModified, runRetrieveSave } = params;
    const numericId = String(opportunityId || '').trim();
    if (!numericId) return false;

    const opProducts = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
    const haveLightreachProduct = Array.isArray(opProducts)
        ? opProducts.some((product: any) => product.Finance_Partner === COMMON_CONSTANTS.LightreachPartnerName)
        : false;

    if (haveLightreachProduct && confirmed && !isEquipmentModified) {
        try {
            await designServices.lightReachVoidQuote(numericId, opportunityDetails?.Finance_Partner_Sync_LeadID);
        } catch (e: unknown) {
            const err = e as {
                response?: { data?: { title?: string; Message?: string; errors?: Record<string, unknown> } };
                message?: string;
            };
            const data = err?.response?.data;
            let text =
                String(data?.title ?? data?.Message ?? err?.message ?? '').trim() || 'Could not void Lightreach quote.';
            if (data?.errors && typeof data.errors === 'object') {
                const parts = Object.values(data.errors).flat();
                if (parts.length > 0) {
                    text = Array.isArray(parts[0]) ? String((parts[0] as string[])[0]) : String(parts[0]);
                }
            }
            message.error(text);
            return false;
        }
    }

    if (!isEquipmentModified) {
        try {
            await quoteServices.contractUpdateLoanStatus(numericId, UPDATE_LOAN_STATUS_SYSTEM_DESIGN_FIELD, 0);
        } catch (e: unknown) {
            const ax = e as { response?: { data?: { title?: string; Message?: string; errors?: unknown } }; message?: string };
            let text =
                String(ax?.response?.data?.title ?? ax?.response?.data?.Message ?? ax?.message ?? '').trim() ||
                'Could not post system design override to Contract.';
            const data = ax?.response?.data as { errors?: Record<string, unknown> } | undefined;
            if (data?.errors && typeof data.errors === 'object') {
                const parts = Object.values(data.errors).flat();
                if (parts.length > 0) text = Array.isArray(parts[0]) ? String((parts[0] as string[])[0]) : String(parts[0]);
            }
            message.error(text);
            return false;
        }
    }

    try {
        await quoteServices.savePageChanges({
            opportunityId: numericId,
            pageName: 'Design',
            changes:
                'Force sync: user confirmed refreshing system design with an active Lightreach or Good Leap change order in a sent, generated, or signed contract state.',
        });
        await quoteServices.getPageChanges(numericId);
    } catch {
        /* Trinity checklist: best-effort */
    }

    try {
        await quoteServices.contractGetLoanStatusByOpportunity(numericId);
    } catch {
        /* Angular continues */
    }

    if (!confirmed || isEquipmentModified) {
        await runRetrieveSave(true, !isEquipmentModified);
        return false;
    }

    useOneButtonStore.getState().setQuotesRefreshTrigger(Date.now());
    return true;
}

type PromptForceSyncParams = {
    opportunityId: string;
    opportunityDetails: any;
    systemDesign: any;
    isBatteryInfoUpdated?: boolean;
    runRetrieveSave: RunRetrieveSaveAfterForceSync;
};

type ForceSyncPromptHostProps = PromptForceSyncParams & {
    isEquipmentModified: boolean;
    onSettled: (value: boolean) => void;
    onUnmount: () => void;
};

const ForceSyncPromptHost: React.FC<ForceSyncPromptHostProps> = ({
    opportunityId,
    opportunityDetails,
    systemDesign,
    isBatteryInfoUpdated = false,
    isEquipmentModified,
    runRetrieveSave,
    onSettled,
    onUnmount,
}) => {
    const [open, setOpen] = useState(true);
    const [loading, setLoading] = useState(false);
    const theme = useOneButtonStore((s) => s.theme);

    const handleFinish = async (confirmed: boolean) => {
        if (loading) return;
        setLoading(true);
        try {
            const proceed = await finishSystemDesignForceSyncConfirm({
                confirmed,
                opportunityId,
                opportunityDetails,
                isEquipmentModified,
                runRetrieveSave,
            });
            setOpen(false);
            onSettled(confirmed ? proceed : false);
        } finally {
            setLoading(false);
        }
    };

    return (
        // This host is mounted on its own `createRoot`, outside `App`'s provider, so the theme must be
        // re-applied here or antd falls back to its defaults (wrong primary, control height and font).
        <ConfigProvider theme={buildAntdThemeConfig(theme, resolveAppPrimaryColor())}>
            <SystemDesignForceSyncConfirmModal
                open={open}
                loading={loading}
                theme={theme}
                systemDesign={systemDesign}
                isBatteryInfoUpdated={isBatteryInfoUpdated}
                isEquipmentModified={isEquipmentModified}
                zIndex={VIEWER_FORCE_SYNC_CONFIRM_Z_INDEX}
                getContainer={() => document.body}
                onOk={() => void handleFinish(true)}
                onCancel={() => void handleFinish(false)}
                afterClose={onUnmount}
            />
        </ConfigProvider>
    );
};

/**
 * Shows the same confirm dialog as `ProductionsCard.handleRefresh` when LR/GL change order requires force sync.
 * Renders `SystemDesignForceSyncConfirmModal` on `document.body` above viewer modals.
 */
export function promptSystemDesignForceSyncConfirm(params: PromptForceSyncParams): Promise<boolean> {
    const { opportunityId, opportunityDetails, systemDesign, isBatteryInfoUpdated = false, runRetrieveSave } = params;
    const isEquipmentModified = resolveIsEquipmentModified(systemDesign, isBatteryInfoUpdated);
    const sceneViewer = useViewerStore.getState().sceneViewer as
        | { viewerAlert: (opts: Record<string, unknown>) => void }
        | null
        | undefined;

    sceneViewer?.viewerAlert?.({ show: false });

    return new Promise((resolve) => {
        let settled = false;
        const container = document.createElement('div');
        document.body.appendChild(container);
        const root = createRoot(container);

        const settle = (value: boolean) => {
            if (settled) return;
            settled = true;
            resolve(value);
        };

        const unmount = () => {
            root.unmount();
            container.remove();
        };

        root.render(
            <ForceSyncPromptHost
                opportunityId={opportunityId}
                opportunityDetails={opportunityDetails}
                systemDesign={systemDesign}
                isBatteryInfoUpdated={isBatteryInfoUpdated}
                isEquipmentModified={isEquipmentModified}
                runRetrieveSave={runRetrieveSave}
                onSettled={settle}
                onUnmount={unmount}
            />,
        );
    });
}
